import { useEffect, useState } from "react";
import { Box, CircularProgress, Fab, styled, Tooltip } from "@mui/material";
import useSession from "hooks/call-queue/useSession";
import { CallSlash, FormatSquare, Location, Microphone2, MicrophoneSlash, RecordCircle } from "iconsax-react";
import { CallStatus, ISupportUserListItem } from "types/auth";
import useFirebase from "hooks/useFirebase";
import useApi from "hooks/useApi";
import ScreenshotModal from "./ScreenshotModal";
import { Popover } from "@mui/material";
import InviteSupportUser from "./InviteSupportUser";
import LocationPopup from "components/LocationPopup";
import { useNavigate } from "react-router";

const BlinkingIcon = styled(RecordCircle)(({ theme }) => ({
    "@keyframes blink": {
        "0%, 100%": { opacity: 1 },
        "50%": { opacity: 0 },
    },
    animation: "blink 1s infinite",
}));


interface SessionActionProps {
    onEndingSession?: (isEnding: boolean) => void;
}

const SessionAction = ({ onEndingSession }: SessionActionProps) => {
    const { session, localAudioTrack, client, remoteVideo, cleanupSession } = useSession();
    const { addCallHistoryEntry, updateMuteStatus, updateLocationStatus, updateRecordingStatus, updateCallStatus } = useFirebase();
    const { user, startSessionRecording, stopSessionRecording, endSession, cancelCall, showToast, getSupportUsers }=  useApi()
    const [isAudioMuted, setIsAudioMuted] = useState(false);
    const [screenshot, setScreenshot] = useState<{ url: string; base64: string; file: File | string } | null>(null);
    const [isLoading, setLoading] = useState(false);
    const [supportUsers, setSupportUsers] = useState<ISupportUserListItem[]>([]);
    const [showLocationPopup, setShowLocationPopup] = useState(false);

    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

    const navigate = useNavigate();

    // const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    //     setAnchorEl(event.currentTarget);
    // };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    const id = open ? 'simple-popover' : undefined;

    const muteOrUnmute = async (isMute: boolean) => {
        console.log(`SessionAction: Attempting to ${isMute ? 'mute' : 'unmute'} audio`);
        
        // Check if we have a valid audio track
        if (!localAudioTrack) {
            console.warn('SessionAction: Audio track not available');
            showToast('Audio track not available', 'warning');
            return;
        }

        try {
            // Check if the audio track is in a valid state
            if (localAudioTrack.trackMediaType !== 'audio') {
                console.error('SessionAction: Invalid audio track type');
                showToast('Invalid audio track', 'error');
                return;
            }

            // Use the correct Agora RTC setEnabled method (returns void/undefined)
            await localAudioTrack.setEnabled(!isMute);
            
            // Update local state
            setIsAudioMuted(isMute);
            console.log(`SessionAction: Audio ${isMute ? 'muted' : 'unmuted'} successfully`);
            
            // Update Firebase with the mute status
            if (session?.transaction && user?.uid) {
                addCallHistoryEntry(session.transaction, {
                    status: isMute ? CallStatus.SUPPORT_MUTE : CallStatus.SUPPORT_UNMUTE,
                    datetime: new Date().toISOString(),
                    user: user.uid
                });

                updateMuteStatus(session.transaction, isMute);
            }
            
            // Show user feedback
            showToast(isMute ? 'Microphone muted' : 'Microphone unmuted', 'success');
            
        } catch (error) {
            console.error('SessionAction: Error toggling audio mute:', error);
            showToast('Failed to toggle microphone', 'error');
            
            // Revert the UI state if the operation failed
            // Don't change setIsAudioMuted since the operation failed
        }
    }

    // Tear down whatever we actually hold. Deliberately tolerant of a missing
    // client: a failed join leaves `client` null, and the old code bailed out of
    // disconnectCall entirely in that case — after the "Ending session…" overlay
    // was already up. That is the page that got stuck on end-call, and it also
    // meant the session was never ended, so the app stayed in a dead call.
    const teardownLocalMedia = () => {
        if (cleanupSession) {
            cleanupSession();
            return;
        }
        try {
            localAudioTrack?.stop();
            localAudioTrack?.close();
        } catch (error) {
            console.warn('SessionAction: Error stopping audio track:', error);
        }
        try {
            if (client?.connectionState === 'CONNECTED') {
                client.unpublish();
                client.leave();
            }
        } catch (error) {
            console.warn('SessionAction: Error leaving Agora channel:', error);
        }
    }

    const disconnectCall = async () => {
        onEndingSession?.(true);

        try{
            // Our own outgoing call, still ringing: End Call means cancel it. The
            // caller never gets `supportAccepted` set (that is stamped when an agent
            // answers an INCOMING call), so without this they fell into the "just
            // leave" branch below — the dashboard went home while the app kept
            // ringing on a call nobody was in any more.
            const iAmCaller = session?.from === user?.uid;
            const ringing = session?.status === CallStatus.CALLING || session?.status === CallStatus.RECEIVING;
            if(iAmCaller && ringing){
                await updateCallStatus(session!.transaction!, CallStatus.MISSED_CALL);
                addCallHistoryEntry(session!.transaction!, {
                    status: CallStatus.MISSED_CALL,
                    datetime: new Date().toISOString(),
                    user: user!.uid
                });
                try { await cancelCall(session!.transaction!); } catch (e) { /* best effort */ }
                teardownLocalMedia();
                showToast('Call cancelled', 'success');
                navigate('/');
                return;
            }

            // Not the agent who owns the session: just leave, the call continues.
            // The caller always owns their own call, answered or not.
            if(!iAmCaller && session?.supportAccepted != user.uid){
                addCallHistoryEntry(session!.transaction!, {
                    status: CallStatus.SUPPORT_LEFT,
                    datetime: new Date().toISOString(),
                    user: user!.uid
                });

                teardownLocalMedia();
                showToast('Exiting the session. Thank you!', 'success');
                navigate('/')
                return;
            }

            let response = await endSession(session?.transaction!);
            let { success, message } = response;

            if(!success){
                showToast(message, 'error');
                return;
            }

            addCallHistoryEntry(session!.transaction!, {
                status: CallStatus.SUPPORT_LEFT,
                datetime: new Date().toISOString(),
                user: user!.uid
            });

            teardownLocalMedia();

            addCallHistoryEntry(session!.transaction!, {
                status: CallStatus.COMPLETED,
                datetime: new Date().toISOString(),
                user: user!.uid
            });

            updateCallStatus(session!.transaction!, CallStatus.COMPLETED);

            showToast('Session has been ended', 'success');
            navigate('/'); // Navigate to home page to ensure complete state reset
        }catch(error: any){
            showToast(error.message, 'error');
        }finally{
            // Always drop the overlay — every early return above used to leave it up.
            onEndingSession?.(false);
        }
    }

    const loadSupportUsers = async () => {

        if(!session) return;

        try{
            let response = await getSupportUsers(session?.transaction!);
            let { success, data } = response;
            if(success){
                setSupportUsers(data);
            }
        }catch(error: any){
            
        }
        
    }

    useEffect(() => {
        loadSupportUsers();
    }, [session]);

    // Initialize mute state based on audio track state
    useEffect(() => {
        if (localAudioTrack) {
            try {
                // Get the current enabled state of the audio track
                const isEnabled = localAudioTrack.enabled;
                setIsAudioMuted(!isEnabled);
                console.log('SessionAction: Initialized mute state:', !isEnabled);
            } catch (error) {
                console.warn('SessionAction: Could not read audio track state:', error);
            }
        }
    }, [localAudioTrack]);

    const requestLocation = () => {
        // Log the location request in call history
        addCallHistoryEntry(session!.transaction!, {
            status: CallStatus.SUPPORT_LOCATION_REQUEST,
            datetime: new Date().toISOString(),
            user: user!.uid
        });
        updateLocationStatus(session!.transaction!, true);
        
        // Show the location popup
        setShowLocationPopup(true);
    }

    const takeScreenshotAsFile = async (): Promise<{ url: string; base64: string; file: File | string } | null> => {
        try {
            // First try to get screenshot from the remote video element that's actually playing
            const remoteVideoElement = document.getElementById("remote-video")?.querySelector('video') as HTMLVideoElement;
            
            if (remoteVideoElement && remoteVideoElement.readyState >= 2 && remoteVideoElement.videoWidth > 0 && remoteVideoElement.videoHeight > 0) {
                // Wait a bit to ensure video frame is ready
                await new Promise(resolve => setTimeout(resolve, 100));
                
                const canvas = document.createElement("canvas");
                canvas.width = remoteVideoElement.videoWidth;
                canvas.height = remoteVideoElement.videoHeight;
                const ctx = canvas.getContext("2d");

                if (!ctx) {
                    console.error("Failed to get canvas context.");
                    return null;
                }

                // Draw the current video frame onto the canvas
                ctx.drawImage(remoteVideoElement, 0, 0, canvas.width, canvas.height);

                // Convert canvas to Base64 and URL
                const base64Image = canvas.toDataURL("image/png", 0.95);
                const fileName = `screenshot-${session?.transaction}-${Date.now()}.png`;

                // Convert base64 to File
                const file = await fetch(base64Image)
                    .then((res) => res.blob())
                    .then((blob) => new File([blob], fileName, { type: "image/png" }));

                return {
                    url: base64Image,
                    base64: base64Image,
                    file,
                };
            }

            // Fallback: Try using remoteVideo track directly if video element isn't ready
            if (remoteVideo && remoteVideo.getMediaStreamTrack() && remoteVideo.getMediaStreamTrack().readyState === 'live') {
                // Create a temporary video element with the remote track
                const videoElement = document.createElement("video");
                videoElement.autoplay = true;
                videoElement.muted = true;
                videoElement.playsInline = true;
                
                const stream = new MediaStream([remoteVideo.getMediaStreamTrack()]);
                videoElement.srcObject = stream;

                // Wait for the video to be ready
                await new Promise<void>((resolve, reject) => {
                    const timeout = setTimeout(() => {
                        reject(new Error("Video loading timeout"));
                    }, 5000);

                    const onReady = () => {
                        if (videoElement.videoWidth > 0 && videoElement.videoHeight > 0) {
                            clearTimeout(timeout);
                            resolve();
                        }
                    };

                    videoElement.addEventListener('loadedmetadata', onReady);
                    videoElement.addEventListener('loadeddata', onReady);
                    
                    // Try to play the video
                    videoElement.play().catch(console.error);
                });

                // Wait a bit more for the first frame to render
                await new Promise(resolve => setTimeout(resolve, 200));

                const canvas = document.createElement("canvas");
                canvas.width = videoElement.videoWidth;
                canvas.height = videoElement.videoHeight;
                const ctx = canvas.getContext("2d");

                if (!ctx) {
                    console.error("Failed to get canvas context.");
                    return null;
                }

                // Draw the current video frame onto the canvas
                ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);

                // Clean up the temporary video element
                videoElement.srcObject = null;

                // Convert canvas to Base64 and URL
                const base64Image = canvas.toDataURL("image/png", 0.95);
                const fileName = `screenshot-${session?.transaction}-${Date.now()}.png`;

                // Convert base64 to File
                const file = await fetch(base64Image)
                    .then((res) => res.blob())
                    .then((blob) => new File([blob], fileName, { type: "image/png" }));

                return {
                    url: base64Image,
                    base64: base64Image,
                    file,
                };
            }

            // No video stream available
            showToast("No video stream available for screenshot", "error");
            return null;

        } catch (error: any) {
            console.error("Screenshot capture failed:", error);
            showToast(`Failed to capture screenshot: ${error.message}`, "error");
            return null;
        }
    }
    
    const getScreenshot = async () => {
        let screenshot = await takeScreenshotAsFile();
        setScreenshot(screenshot);
    }
    
    const startRecording = async () => {
        setLoading(true);
        try{
            let userTokenObj = session?.session?.tokens.find(token => token.uid == user.uid);

            if(!userTokenObj) return;

            let response = await startSessionRecording(session?.transaction!, {
                path: `transaction/${session?.session?.keyId}/recording`,
                uid: userTokenObj.uidNumber
            });
            let { success, message } = response;
    
            if(success){
                updateRecordingStatus(session?.transaction!, true);
                addCallHistoryEntry(session!.transaction!, {
                    status: CallStatus.SUPPORT_RECORD_START,
                    datetime: new Date().toISOString(),
                    user: user!.uid
                });
                showToast('Recording is Started', 'success');
            }else{
                showToast(message, 'error');
            }

        }catch(error: any){
            showToast(error.message, 'error');
        }finally{
            setLoading(false);
        }
    }

    const endRecording = async () => {
        setLoading(true);
        try{
            let userTokenObj = session?.session?.tokens.find(token => token.uid == user.uid);

            if(!userTokenObj) return;

            let response = await stopSessionRecording(session?.transaction!, {
                uid: userTokenObj.uidNumber!
            });
            let { success, message } = response;
    
            if(success){
                updateRecordingStatus(session?.transaction!, false);
                addCallHistoryEntry(session!.transaction!, {
                    status: CallStatus.SUPPORT_RECORD_STOP,
                    datetime: new Date().toISOString(),
                    user: user!.uid
                });
                showToast('Recording saved — find it under Recordings in this transaction\'s call history', 'success');
            }else{
                showToast(message, 'error');
            }
        }catch(error: any){
            showToast(error.message, 'error');
        }finally{
            setLoading(false);
        }
        
    }

    return (
        <>
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center',
                gap: 1,
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                backdropFilter: 'blur(10px)',
                borderRadius: '24px',
                padding: '8px 16px',
                border: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
                {/* Mute/Unmute Audio */}
                <Tooltip title={isAudioMuted ? 'Unmute Audio' : 'Mute Audio'} placement="top">
                    <Fab 
                        onClick={() => muteOrUnmute(!isAudioMuted)} 
                        color={isAudioMuted ? 'error' : 'primary'} 
                        size="small"
                        sx={{ minWidth: 40, width: 40, height: 40 }}
                        disabled={!localAudioTrack} // Disable button if no audio track
                    >
                        {isAudioMuted ? (
                            <MicrophoneSlash size="20" color="#fff" />
                        ) : (
                            <Microphone2 size="20" color="#fff" />
                        )}
                    </Fab>
                </Tooltip>

                {/* Add Participant */}
                {/* <Tooltip title="Add Participant" placement="top">
                    <Fab 
                        aria-describedby={id} 
                        onClick={handleClick} 
                        color="primary" 
                        size="small"
                        sx={{ minWidth: 40, width: 40, height: 40 }}
                    >
                        <CallAdd size="20" color="#fff" />
                    </Fab>
                </Tooltip> */}

                {/* Recording */}
                {!session?.isRecording ? (
                    <Tooltip title="Start Recording" placement="top">
                        <Fab 
                            onClick={startRecording} 
                            color="info" 
                            size="small" 
                            disabled={isLoading}
                            sx={{ minWidth: 40, width: 40, height: 40 }}
                        >
                            {isLoading ? (
                                <CircularProgress size={20} sx={{ color: 'white' }} />
                            ) : (
                                <RecordCircle size="20" color="#fff" />
                            )}
                        </Fab>
                    </Tooltip>
                ) : (
                    <Tooltip title="Stop Recording" placement="top">
                        <Fab 
                            onClick={endRecording} 
                            color="error" 
                            size="small" 
                            disabled={isLoading}
                            sx={{ minWidth: 40, width: 40, height: 40 }}
                        >
                            {isLoading ? (
                                <CircularProgress size={20} sx={{ color: 'white' }} />
                            ) : (
                                <BlinkingIcon size="20" color="#fff" />
                            )}
                        </Fab>
                    </Tooltip>
                )}

                {/* Screenshot */}
                <Tooltip title="Take Screenshot" placement="top">
                    <Fab 
                        onClick={getScreenshot} 
                        color="info" 
                        size="small"
                        sx={{ minWidth: 40, width: 40, height: 40 }}
                    >
                        <FormatSquare size="20" color="#fff" />
                    </Fab>
                </Tooltip>

                {/* Location */}
                <Tooltip title="Get User Location" placement="top">
                    <Fab 
                        onClick={requestLocation} 
                        color="info" 
                        size="small"
                        sx={{ minWidth: 40, width: 40, height: 40 }}
                    >
                        <Location size="20" color="#fff" />
                    </Fab>
                </Tooltip>

                {/* End Call */}
                <Tooltip title="End Call" placement="top">
                    <Fab
                        onClick={disconnectCall}
                        color="error"
                        size="small"
                        sx={{ minWidth: 40, width: 40, height: 40 }}
                    >
                        <CallSlash size="20" color="#fff" />
                    </Fab>
                </Tooltip>
            </Box>

            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
            >
                <InviteSupportUser {...{ supportUsers, handleClose }} />
            </Popover>

            {screenshot && <ScreenshotModal screenshot={screenshot} setScreenshot={setScreenshot} />}
            
            <LocationPopup
                open={showLocationPopup}
                onClose={() => setShowLocationPopup(false)}
                sessionData={{
                    fromName: session?.fromName,
                    key: session?.key
                }}
            />
        </>
    )
}

export default SessionAction;